import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  actionKeys,
  createAction,
  createRule,
  deleteAction,
  deleteRule,
  freeId,
  getAction,
  getRule,
  listActions,
  listRules,
  putAction,
  putRule,
  setActionEnabled,
  setRuleEnabled,
  stripServerFields,
  type ActionDef,
  type InteractionRule,
  type RegistryKind,
} from '@/api/actions'
import { toast } from '@/components/ui/toast'

/**
 * The write side of the Actions page, shared by the cards and the detail pages: the enabled switch
 * (optimistic), save from the editors, create, duplicate, delete, reset a changed built-in.
 * Actions and world rules go through the same hooks with `kind`.
 */

type Doc = ActionDef | InteractionRule

const detailKey = (kind: RegistryKind, id: string) => (kind === 'rule' ? actionKeys.rule(id) : actionKeys.detail(id))
const listPrefix = (kind: RegistryKind) => (kind === 'rule' ? [...actionKeys.rules(), 'list'] : [...actionKeys.all(), 'list'])

export function invalidateLists(qc: QueryClient, kind: RegistryKind): Promise<void> {
  return qc.invalidateQueries({ queryKey: listPrefix(kind) })
}

/** Store a fresh server doc: the detail cache, and its row in every cached list. */
export function storeDoc(qc: QueryClient, kind: RegistryKind, doc: Doc): void {
  qc.setQueryData(detailKey(kind, doc.id), doc)
  qc.setQueriesData<Doc[]>({ queryKey: listPrefix(kind) }, (rows) => (Array.isArray(rows) ? rows.map((r) => (r.id === doc.id ? { ...r, ...doc } : r)) : rows))
}

/** The enabled switch. Flips at once; a failure flips it back and says why. */
export function useToggleEnabled(kind: RegistryKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }): Promise<Doc> => (kind === 'rule' ? setRuleEnabled(id, enabled) : setActionEnabled(id, enabled)),
    onMutate: async ({ id, enabled }) => {
      await qc.cancelQueries({ queryKey: listPrefix(kind) })
      const lists = qc.getQueriesData<Doc[]>({ queryKey: listPrefix(kind) })
      const detail = qc.getQueryData<Doc>(detailKey(kind, id))
      qc.setQueriesData<Doc[]>({ queryKey: listPrefix(kind) }, (rows) => (Array.isArray(rows) ? rows.map((r) => (r.id === id ? { ...r, enabled } : r)) : rows))
      if (detail) qc.setQueryData(detailKey(kind, id), { ...detail, enabled })
      return { lists, detail }
    },
    onError: (err, { id, enabled }, ctx) => {
      for (const [key, data] of ctx?.lists ?? []) qc.setQueryData(key, data)
      if (ctx?.detail) qc.setQueryData(detailKey(kind, id), ctx.detail)
      toast.error(enabled ? 'Could not switch it on' : 'Could not switch it off', { description: err instanceof Error ? err.message : 'Please try again.' })
    },
    onSuccess: (doc) => storeDoc(qc, kind, doc),
  })
}

/** Save from the JSON editor: `PUT {base_rev, body}`. The caller shows the error (422 lines, 409). */
export function useSaveDoc(kind: RegistryKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, baseRev, body }: { id: string; baseRev: number; body: Record<string, unknown> }): Promise<Doc> =>
      kind === 'rule' ? putRule(id, baseRev, body) : putAction(id, baseRev, body),
    onSuccess: (doc) => {
      storeDoc(qc, kind, doc)
      void invalidateLists(qc, kind)
    },
  })
}

/** Duplicate as the parent's own copy (`<id>_copy`, "<label> (copy)"). Resolves with the new doc. */
export function useDuplicate(kind: RegistryKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (doc: Doc): Promise<Doc> => {
      const taken = (kind === 'rule' ? await listRules() : await listActions()).map((d) => d.id)
      const body = stripServerFields(doc as unknown as Record<string, unknown>)
      body.id = freeId(doc.id, [...taken, doc.id])
      const label = typeof doc.label === 'string' && doc.label ? doc.label : null
      if (label) body.label = `${label} (copy)`
      return kind === 'rule' ? createRule(body) : createAction(body)
    },
    onSuccess: (doc) => {
      qc.setQueryData(detailKey(kind, doc.id), doc)
      void invalidateLists(qc, kind)
      toast.success('Copied', { description: 'The copy is yours to change.' })
    },
    onError: (err) => toast.error('Could not copy it', { description: err instanceof Error ? err.message : 'Please try again.' }),
  })
}

/** Create from the manual editor (`POST`). The caller shows the error (422 lines, 409 = id taken). */
export function useCreateDoc(kind: RegistryKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>): Promise<Doc> => (kind === 'rule' ? createRule(stripServerFields(body)) : createAction(stripServerFields(body))),
    onSuccess: (doc) => {
      qc.setQueryData(detailKey(kind, doc.id), doc)
      void invalidateLists(qc, kind)
    },
  })
}

/**
 * "Reset to original": deleting the parent's changed copy of a built-in brings the built-in back
 * (the server answers `reverted`); the detail is then read again so the page shows the original.
 */
export function useResetDoc(kind: RegistryKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<Doc> => {
      await (kind === 'rule' ? deleteRule(id) : deleteAction(id))
      return kind === 'rule' ? getRule(id) : getAction(id)
    },
    onSuccess: (doc) => {
      storeDoc(qc, kind, doc)
      void invalidateLists(qc, kind)
    },
  })
}

export function useDeleteDoc(kind: RegistryKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string): Promise<void> => (kind === 'rule' ? deleteRule(id) : deleteAction(id)),
    onSuccess: (_v, id) => {
      qc.removeQueries({ queryKey: detailKey(kind, id) })
      qc.setQueriesData<Doc[]>({ queryKey: listPrefix(kind) }, (rows) => (Array.isArray(rows) ? rows.filter((r) => r.id !== id) : rows))
      void invalidateLists(qc, kind)
    },
  })
}
